import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, ElementRef,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { componentDestroyed, Game, Playoff, PlayoffTypes, Sport, UntilDestroy } from '@mtgame/core';
import { TournamentPlayoffWidgetService } from './tournament-playoff-widget.service';
import { filter, take, takeUntil } from 'rxjs/operators';
import { getGameRoute } from '@config/routes';
import { ViewportService } from '@core/services/viewport.service';

@UntilDestroy
@Component({
  selector: 'mtg-tournament-playoff-widget',
  templateUrl: './tournament-playoff-widget.component.html',
  styleUrls: ['./tournament-playoff-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    TournamentPlayoffWidgetService,
  ]
})
export class TournamentPlayoffWidgetComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input()
  playoff: Playoff;
  @Input()
  titleVisible = true;
  @Input()
  sport: Sport;
  gameRoute = getGameRoute().join('/');
  games: Game[];
  winnerBracketGames: Game[];
  loserBracketGames: Game[];
  playoffTypes = PlayoffTypes;
  activeRound: number;
  winnerBracketActiveRound: number;
  loserBracketActiveRound: number;
  loaded: boolean;

  constructor(
    private tournamentPlayoffWidgetService: TournamentPlayoffWidgetService,
    private elementRef: ElementRef,
    private viewportService: ViewportService,
    private cdr: ChangeDetectorRef,
  ) {
  }

  async ngOnInit(): Promise<void> {
    this.tournamentPlayoffWidgetService.games$
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(games => {
        this.games = games;
        this.winnerBracketGames = this.games.filter(item => item.playoffNumber.substring(0, 1) !== 'l');
        this.loserBracketGames = this.games.filter(item => item.playoffNumber.substring(0, 1) === 'l');

        this.activeRound = this.getActiveRound(this.games);
        this.winnerBracketActiveRound = this.getActiveRound(this.winnerBracketGames);
        this.loserBracketActiveRound = this.getActiveRound(this.loserBracketGames);
        this.cdr.markForCheck();
      })
  }

  ngAfterViewInit() {
    this.viewportService.track(this.elementRef.nativeElement)
      .pipe(
        filter(value => Boolean(value)),
        takeUntil(componentDestroyed(this)),
        take(1)
      ).subscribe(async () => {
        await this.tournamentPlayoffWidgetService.initialize(this.playoff.id);
        this.loaded = true;
      });
  }

  ngOnDestroy(): void {
    this.viewportService.deleteElement(this.elementRef.nativeElement);
    this.tournamentPlayoffWidgetService.dispose();
  }

  getActiveRound(games: Game[]): number {
    const inProgressRounds = games.filter(g => !g.isClosed && g.team.id && g.competitorTeam.id).map(g => {
      const numberParts = g.playoffNumber.split('_');
      return numberParts.length > 2 ? +numberParts[1] : +numberParts[0];
    });
    if (inProgressRounds.length === 0) {
      return null;
    }
    return Math.min(...inProgressRounds);
  }
}
